import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { transformProject } from '../workspace/utils';
import {
  transformInvitedUsers,
  transformItems,
  transformMessages,
  transformProjectUsers,
  transformTask,
  transformTasks,
  type ProjectUser,
  type TransformedTask,
  type TransformedMessage,
  type TransformedResource,
  type RawTask,
} from './utils';

export interface ProjectState {
  users: ProjectUser[];
  invitedUsers: ReturnType<typeof transformInvitedUsers>;
  error: boolean;
  loading: boolean;
  modalOpen: boolean;
  projectId: string | null;
  title: string | null;
  currentCredentials: { identityId: string | null };
  authorization: string | null;
  header: string | null;
  userRole: string | null;
  guestHash: string | null;
  items: TransformedResource[] | null;
  tasks: TransformedTask[] | null;
  messages: TransformedMessage[] | null;
  selectedItem: TransformedResource | null;
  details: ReturnType<typeof transformProject> | null;
  projectError?: boolean;
  projectLoading?: boolean;
}

const initialState: ProjectState = {
  users: [],
  invitedUsers: [],
  error: false,
  loading: false,
  modalOpen: false,
  projectId: null,
  title: null,
  currentCredentials: { identityId: null },
  authorization: null,
  header: null,
  userRole: null,
  guestHash: null,
  items: null,
  tasks: null,
  messages: null,
  selectedItem: null,
  details: null,
};

export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    updateProjectUsers(state, action: PayloadAction<unknown[] | null>) {
      state.users = action.payload
        ? transformProjectUsers(action.payload as Parameters<typeof transformProjectUsers>[0])
        : [];
      // Re-apply userData to messages/tasks that arrived before users
      if (state.users.length > 0) {
        if (state.messages) {
          state.messages = state.messages.map((m) => ({
            ...m,
            userData: state.users.find((u) => u.uid === m.sender),
          }));
        }
        if (state.tasks) {
          state.tasks = state.tasks.map((t) => ({
            ...t,
            userData: state.users.find((u) => u.uid === t.owner),
            messages: t.messages.map((msg) => ({
              ...msg,
              userData: state.users.find((u) => u.uid === msg.sender),
            })),
          }));
        }
      }
    },
    updateInvitedUsers(state, action: PayloadAction<unknown[] | null>) {
      state.invitedUsers = action.payload
        ? transformInvitedUsers(action.payload as Parameters<typeof transformInvitedUsers>[0])
        : [];
    },
    updateProjectId(state, action: PayloadAction<string | null>) {
      state.projectId = action.payload;
    },
    updateProjectError(state, action: PayloadAction<boolean>) {
      state.projectError = action.payload;
    },
    updateProjectLoading(state, action: PayloadAction<boolean>) {
      state.projectLoading = action.payload;
    },
    updateProjectModal(state, action: PayloadAction<boolean>) {
      state.modalOpen = action.payload;
    },
    updateAuthorization(state, action: PayloadAction<string | null>) {
      state.authorization = action.payload;
    },
    updateHeader(state, action: PayloadAction<string | null>) {
      state.header = action.payload;
    },
    updateUserRole(state, action: PayloadAction<string | null>) {
      state.userRole = action.payload;
    },
    setItems(state, action: PayloadAction<unknown[] | null>) {
      state.items = action.payload
        ? transformItems(action.payload as Parameters<typeof transformItems>[0])
        : null;
    },
    setTasks(state, action: PayloadAction<RawTask[] | null>) {
      state.tasks = action.payload ? transformTasks(action.payload, state.users) : null;
    },
    setMessages(state, action: PayloadAction<unknown[] | null>) {
      state.messages = action.payload
        ? transformMessages(action.payload as Parameters<typeof transformMessages>[0], state.users)
        : null;
    },
    addMessage(state, action: PayloadAction<TransformedMessage>) {
      state.messages = [...(state.messages ?? []), action.payload];
    },
    removeMessage(state, action: PayloadAction<string>) {
      if (!state.messages) return;
      state.messages = state.messages.filter((m) => m.id !== action.payload);
    },
    removeTask(state, action: PayloadAction<string>) {
      if (!state.tasks) return;
      state.tasks = state.tasks.filter((t) => t.id !== action.payload);
    },
    setSelectedItem(state, action: PayloadAction<TransformedResource | null>) {
      state.selectedItem = action.payload;
    },
    setProjectMetadata(state, action: PayloadAction<{ id?: { S: string }; title?: { S: string }; guestHash?: string; [k: string]: unknown }>) {
      state.details = transformProject(action.payload as Parameters<typeof transformProject>[0]);
      state.projectId = action.payload.id?.S ?? null;
      state.title = action.payload.title?.S ?? null;
      state.guestHash = action.payload.guestHash ?? null;
    },
    updateItemPreview(state, action: PayloadAction<TransformedResource>) {
      if (!state.items) return;
      const item = action.payload;
      state.items = state.items.map((i) =>
        i.id === item.id ? { ...item, preview: !item.preview } : i,
      );
    },
    setItemsPreview(state) {
      if (!state.items) return;
      state.items = state.items.map((i) => ({ ...i, preview: true }));
    },
    addTask(state, action: PayloadAction<RawTask>) {
      if (!state.tasks) state.tasks = [];
      state.tasks = [...state.tasks, transformTask(action.payload, state.users)];
    },
    addTransformedTask(state, action: PayloadAction<TransformedTask>) {
      if (!state.tasks) state.tasks = [];
      state.tasks = [...state.tasks, action.payload];
    },
    replaceTask(state, action: PayloadAction<{ tempId: string; rawTask: RawTask }>) {
      if (!state.tasks) return;
      const { tempId, rawTask } = action.payload;
      const newTask = transformTask(rawTask, state.users);
      state.tasks = state.tasks.map((t) => (t.id === tempId ? newTask : t));
    },
    addMessageToTask(state, action: PayloadAction<{ task: TransformedTask; message: { content: string; created: string; sender: string; userData?: ProjectUser } }>) {
      if (!state.tasks) return;
      const { task, message } = action.payload;
      state.tasks = state.tasks.map((t) =>
        t.id === task.id ? { ...task, messages: [...task.messages, message] } : t,
      );
    },
    updateTaskDuration(state, action: PayloadAction<TransformedTask>) {
      if (!state.tasks) return;
      const task = action.payload;
      state.tasks = state.tasks.map((t) => (t.id === task.id ? task : t));
    },
    addInvitedUser(state, action: PayloadAction<ReturnType<typeof transformInvitedUsers>[number]>) {
      state.invitedUsers = [...state.invitedUsers, action.payload];
    },
    removeInvitedUser(state, action: PayloadAction<string>) {
      state.invitedUsers = state.invitedUsers.filter((i) => i.uid !== action.payload);
    },
    removeUser(state, action: PayloadAction<string>) {
      state.users = state.users.filter((u) => u.uid !== action.payload);
    },
    addUser(state, action: PayloadAction<ProjectUser>) {
      state.users = [...state.users, action.payload];
    },
  },
});

export const {
  updateProjectUsers,
  updateInvitedUsers,
  updateProjectId,
  updateProjectError,
  updateProjectLoading,
  updateProjectModal,
  updateAuthorization,
  updateHeader,
  updateUserRole,
  setItems,
  setTasks,
  setMessages,
  setSelectedItem,
  setProjectMetadata,
  updateItemPreview,
  setItemsPreview,
  addTask,
  addTransformedTask,
  replaceTask,
  addMessageToTask,
  updateTaskDuration,
  addMessage,
  addInvitedUser,
  removeInvitedUser,
  removeUser,
  addUser,
  removeMessage,
  removeTask,
} = projectSlice.actions;

export default projectSlice.reducer;
